import {
  Dispatch,
  useState
} from 'react';
import {useTranslation} from 'react-i18next';
import {useApiTranslations} from '../../../../context/ApiTranslationsContext';
import {OrganizationUserController} from '../../../../api/organization/organization-user.controller';
import {SupportPlanTaskController} from '../../../../api/support/support-plan-task.controller';
import {SupportTicketController} from '../../../../api/support/support-ticket.controller';
import {
  useAsyncEffect,
  useInitialLoad
} from '../../../../hook/use-initial-load';
import {OrganizationUserModel} from '../../../../model/organization/organization-user.model';
import {SupportPlanTask} from '../../../../model/support/task/support-plan-task';
import {SupportPlanTaskType} from '../../../../model/support/task/support-plan-task-type.enum';
import {
  Searchable,
  SearchableFilter,
} from '../../../../state/searchable-state';
import {LazyAutocompleteOption} from '../components/LazyAutocomplete';
import CreateSupportPlanTaskReq = SupportPlanTaskController.CreateSupportPlanTaskReq;
import QuerySupportTicketsRes = SupportTicketController.QuerySupportTicketsRes;

export type SupportTaskCreateDrawerState = {
  translate: (key: string) => string
  task: SupportPlanTask
  setTask: (data: SupportPlanTask) => void

  ticketsData: QuerySupportTicketsRes | null
  fetchTicketsOptions: (page: number, search: string, pageSize: number) => Promise<LazyAutocompleteOption[]>;
  users: OrganizationUserModel[]

  handleSubmit: () => Promise<void>
  onDeletePressed: () => Promise<void>
}

export const useSupportTaskUpdateDrawerState = (initialTask: SupportPlanTask, organizationId: string, supportPlanId: string): SupportTaskCreateDrawerState => {
  const {t} = useTranslation()
  const {translate: apiTranslate} = useApiTranslations()
  const [task, setTask] = useState<SupportPlanTask>(initialTask)

  const [ticketsData, setTicketsData] = useState<QuerySupportTicketsRes | null>(null)
  const users = useInitialLoad(async () => await fetchOrganizationUsers(organizationId)) ?? []

  return {
    users: users,
    task: task,
    ticketsData: ticketsData,
    setTask: setTask,
    handleSubmit: async () => {
      await SupportPlanTaskController.updateSupportPlanTask(organizationId, supportPlanId, task.id, task.version, task)
      setTask({...task, version: task.version + 1})
    },
    onDeletePressed: async () => {
      await SupportPlanTaskController.deleteSupportPlanTask(task.id, task.version)
    },
    fetchTicketsOptions: async (page, search, pageSize) => {
      const result = await fetchSupportTickets(task.ticketId, page, search, pageSize)
      setTicketsData(result)

      return result.items.map(it => ({id: it.id, name: it.title}))
    },
    translate: (key) => apiTranslate(key) || t(key),
  }
}

export const fetchSupportTickets = async (ticketId: string | null, page: number, search: string, pageSize: number): Promise<QuerySupportTicketsRes> => {
  const includeSearchFilter = search != null && search !== ''
  const includeTicketIdFilter = ticketId !== null

  const searchFilter: SearchableFilter = {
    column: "support_ticket.title",
    searchOperator: "ILIKE",
    values: [`%${search}%`]
  }
  const ticketIdFilter: SearchableFilter = {
    column: "support_ticket.id",
    searchOperator: "IN",
    values: [`${ticketId}`]
  }

  let filters: SearchableFilter[] = []
  if(includeSearchFilter) {
    filters = [...filters, searchFilter]
  }
  if(includeTicketIdFilter) {
    filters = [...filters, ticketIdFilter]
  }

  console.log(`Filters: ${JSON.stringify(filters)}`)

  const searchable: Searchable = {
    fullTextSearch: null,
    filters: [],
    groupedFilters: [],
    filtersGroups: [
      {
        filterGroupType: 'AND',
        filters: filters
      }
    ],
    ordering: [],
    page: page,
    pageSize: pageSize,
    availableValues: []
  };

  return (await SupportTicketController.querySupportTickets(searchable))
}

const fetchOrganizationUsers = async (organizationId: string): Promise<OrganizationUserModel[]> =>
  (await OrganizationUserController.queryOrganizationUsers(organizationId))